import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class F1065SchKService {

  constructor(private http: HttpClient) { }

  /**
   * Load main form data via loadPivotFormJson
   */
  loadFormData(
    baseUrl: string,
    rowData: any,
    globalParams: any
  ): Observable<any> {
    const url = `${baseUrl}/loadPivotFormJson`;
    const params: any = {
      action_id: '31259',
      tax_year: (rowData.TAX_YEAR ?? globalParams.tax_year ?? '').toString(),
      scenario: (rowData.SCENARIO_KEY ?? globalParams.scenario ?? '').toString(),
      jcd_key: (rowData.JCD_KEY ?? globalParams.jcd_key ?? '').toString(),
      combination_key: (rowData.COMBINATION_KEY ?? '').toString(),
      schedule_name: 'K',
      part_no: 'NA',
      activity_key: globalParams.activity_key ?? '',
      is_issue_key_enabled: globalParams.is_issue_key_enabled ?? 'N',
      is_locked: globalParams.is_locked ?? 'N',
      issue_id: globalParams.issue_id ?? '',
      issue_id_for_tx_save: globalParams.issue_id_for_tx_save ?? '',
      issue_short_desc: globalParams.issue_short_desc ?? '',
      jcd_ta_key: globalParams.jcd_ta_key ?? '',
      parent_me_string: globalParams.parent_me_string ?? '',
      scenarioCode: globalParams.scenarioCode ?? 'RAF',
      screen_key: globalParams.screen_key ?? '',
      screen_type: globalParams.screen_type ?? 'grid',
    };

    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Load subtable records for line 20c (action_code: z57kmr)
   */
  loadSubtableRecords(
    baseUrl: string,
    rowData: any,
    globalParams: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    const params: any = {
      action_code: 'z57kmr',
      combination_key: (rowData.COMBINATION_KEY ?? '').toString(),
      schedule: 'K',
      activity_key: globalParams.activity_key ?? '',
      is_issue_key_enabled: globalParams.is_issue_key_enabled ?? 'N',
      is_locked: globalParams.is_locked ?? 'N',
      issue_id: globalParams.issue_id ?? '',
      issue_id_for_tx_save: globalParams.issue_id_for_tx_save ?? '',
      issue_short_desc: globalParams.issue_short_desc ?? '',
      jcd_key: rowData.JCD_KEY ?? globalParams.jcd_key ?? '',
      jcd_ta_key: globalParams.jcd_ta_key ?? '',
      parent_me_string: globalParams.parent_me_string ?? '',
      scenario: rowData.SCENARIO_KEY ?? globalParams.scenario ?? '',
      scenarioCode: globalParams.scenarioCode ?? 'RAF',
      screen_key: globalParams.screen_key ?? '',
      screen_type: globalParams.screen_type ?? 'grid',
      tax_year: rowData.TAX_YEAR ?? globalParams.tax_year ?? '',
    };

    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Load options list for dropdown (year >= 2024) - action_code: qd5skt
   */
  loadOptionsList(
    baseUrl: string,
    globalParams: any,
    rowData: any,
    ssoId: string
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    const params: any = {
      action_code: 'qd5skt',
      sso_id: ssoId,
      tax_year: (rowData.TAX_YEAR ?? globalParams.tax_year ?? '').toString(),
      activity_key: globalParams.activity_key ?? '',
      is_issue_key_enabled: globalParams.is_issue_key_enabled ?? 'N',
      is_locked: globalParams.is_locked ?? 'N',
      issue_id: globalParams.issue_id ?? '',
      issue_id_for_tx_save: globalParams.issue_id_for_tx_save ?? '',
      issue_short_desc: globalParams.issue_short_desc ?? '',
      jcd_key: rowData.JCD_KEY ?? globalParams.jcd_key ?? '',
      jcd_ta_key: globalParams.jcd_ta_key ?? '',
      parent_me_string: globalParams.parent_me_string ?? '',
      scenario: rowData.SCENARIO_KEY ?? globalParams.scenario ?? '',
      scenarioCode: globalParams.scenarioCode ?? 'RAF',
      screen_key: globalParams.screen_key ?? '',
      screen_type: globalParams.screen_type ?? 'grid',
    };

    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Save Schedule K form data
   */
  saveScheduleKForm(
    baseUrl: string,
    formData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {},
    processName: string = 'Schedule K'
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=tkgaam`;

    const jsonSettings = {
      tax_year: rowData.TAX_YEAR,
      scenario: rowData.SCENARIO_KEY,
      jcd_key: rowData.JCD_KEY,
      combination_key: rowData.COMBINATION_KEY,
      trans_type_key: formData[0]?.editrows?.[0]?.[0]?.trans_type_key || ''
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const mergedParams = { ...globalService?.globalParams, ...filterParams };
    const payloadParts: string[] = [];

    const encode = (key: string, value: any) => {
      if (value === null || value === undefined) {
        return `${encodeURIComponent(key)}=`;
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    };

    payloadParts.push(encode('jsonSettings', JSON.stringify(jsonSettings)));
    payloadParts.push(encode('jsonObj', JSON.stringify(formData)));

    Object.keys(mergedParams).forEach(key => {
      if (key === 'process_name' || key === 'combination_key' || key === 'trans_type_key' || key === 'checkLockedbj') {
        return;
      }
      const value = mergedParams[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          payloadParts.push(encode(key, JSON.stringify(value)));
        } else {
          payloadParts.push(encode(key, value.toString()));
        }
      } else {
        payloadParts.push(encode(key, ''));
      }
    });

    payloadParts.push(encode('checkLockedbj', JSON.stringify([
      {
        leid: rowData.LEID,
        combination_key: rowData.COMBINATION_KEY,
        scenario_key: rowData.SCENARIO_KEY
      }
    ])));
    payloadParts.push(encode('process_name', processName));
    payloadParts.push(encode('combination_key', rowData.COMBINATION_KEY.toString()));
    payloadParts.push(encode('trans_type_key', jsonSettings.trans_type_key));

    const payloadString = payloadParts.join('&');

    return this.http.post<any>(url, payloadString, {
      headers,
      withCredentials: true
    });
  }
}
